import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Minus, Plus, MapPin, Calendar, Clock, ArrowLeft, ArrowRight, Loader2, Check,
  ShoppingBasket, CheckCircle2, Phone, Mail, Printer,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) => `${Number(v).toFixed(2).replace(".", ",")} €`;

const STEPS = ["Produits", "Retrait", "Date", "Coordonnées", "Vérification"];

const genKey = () => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const Commande = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [step, setStep] = useState(0);

  const [qty, setQty] = useState({}); // product_id -> qty
  const [pointId, setPointId] = useState(null);
  const [dateId, setDateId] = useState(null);
  const [client, setClient] = useState({ prenom: "", nom: "", telephone: "", email: "" });
  const [accVerif, setAccVerif] = useState(false);
  const [accPaie, setAccPaie] = useState(false);
  const [accRgpd, setAccRgpd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const idemKey = useRef(genKey());

  useEffect(() => {
    axios.get(`${API}/order/campaign/active`)
      .then((r) => setData(r.data))
      .catch(() => setData({ active: false }))
      .finally(() => setLoading(false));
  }, []);

  const selectedPoint = useMemo(
    () => data?.points?.find((p) => p.id === pointId) || null, [data, pointId]
  );

  const items = useMemo(() => {
    if (!data?.products) return [];
    return data.products
      .filter((p) => (qty[p.id] || 0) > 0)
      .map((p) => ({ ...p, quantite: qty[p.id], sous_total: p.prix * qty[p.id] }));
  }, [data, qty]);

  const total = useMemo(() => items.reduce((s, it) => s + it.sous_total, 0), [items]);

  const setQ = (id, v) => setQty((q) => ({ ...q, [id]: Math.max(0, (q[id] || 0) + v) }));

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(client.email);
  const validPhone = client.telephone.replace(/[^0-9+]/g, "").length >= 8;
  const clientOk = client.prenom.trim() && client.nom.trim() && validPhone && validEmail;

  const canNext = () => {
    if (step === 0) return items.length > 0;
    if (step === 1) return !!pointId;
    if (step === 2) return !!dateId;
    if (step === 3) return clientOk;
    return true;
  };

  const next = () => {
    if (step === 0 && items.length === 0) {
      toast.error("Veuillez sélectionner au moins un produit pour continuer.");
      return;
    }
    if (step === 1 && !pointId) { toast.error("Veuillez choisir un point de retrait."); return; }
    if (step === 2 && !dateId) { toast.error("Veuillez choisir une date de retrait."); return; }
    if (step === 3 && !clientOk) { toast.error("Merci de renseigner correctement vos coordonnées."); return; }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const back = () => { setStep((s) => Math.max(0, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const selectedDate = selectedPoint?.dates?.find((d) => d.id === dateId) || null;

  const submit = async () => {
    if (submitting) return;
    if (!accVerif || !accPaie) { toast.error("Merci de cocher les cases de confirmation obligatoires."); return; }
    setSubmitting(true);
    try {
      const { data: order } = await axios.post(`${API}/order`, {
        campaign_id: data.campaign.id,
        point_id: pointId,
        date_id: dateId,
        items: items.map((it) => ({ product_id: it.id, quantite: it.quantite })),
        client,
        accept_verif: accVerif,
        accept_paiement_place: accPaie,
        idempotency_key: idemKey.current,
      });
      setConfirmed(order);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Une erreur est survenue. Merci de réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-creme flex items-center justify-center text-mousse"><Loader2 className="animate-spin mr-2" /> Chargement de la boutique...</div>;
  }

  if (!data?.active) {
    return (
      <div className="min-h-screen bg-creme flex flex-col items-center justify-center px-6 text-center">
        <ShoppingBasket size={48} className="text-bordeaux/40" />
        <h1 className="font-heading text-3xl text-foreground mt-4">Aucune campagne de commande en cours</h1>
        <p className="text-foreground/65 mt-3 max-w-md">Les commandes en ligne ne sont pas ouvertes pour le moment. Contactez-nous directement, nous serons ravis de vous renseigner.</p>
        <button onClick={() => navigate("/")} data-testid="commande-home-btn" className="mt-6 rounded-full bg-bordeaux text-white font-600 px-7 py-3 hover:bg-bordeaux-dark transition-colors">Retour au site</button>
      </div>
    );
  }

  // -------- Confirmation page --------
  if (confirmed) {
    const printOrder = () => {
      const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
      const rows = confirmed.items.map((it) =>
        `<tr><td>${it.quantite}× ${esc(it.nom)} <span style="color:#8a8378">(${esc(it.conditionnement)})</span></td><td style="text-align:right;white-space:nowrap">${fmt(it.sous_total)}</td></tr>`
      ).join("");
      const win = window.open("", "_blank");
      win.document.write(`<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>Commande ${confirmed.numero} — Escarg'Holala</title>
        <style>
          body{font-family:Georgia,serif;color:#2C2A28;padding:36px;max-width:640px;margin:0 auto}
          h1{color:#722F37;margin:0 0 2px;font-size:26px} .num{color:#722F37;font-size:22px;font-weight:700;margin:10px 0}
          .lbl{font-size:11px;letter-spacing:2px;text-transform:uppercase;color:#6b8e5a;margin:18px 0 4px}
          table{width:100%;border-collapse:collapse;margin-top:6px} td{padding:6px 0;border-bottom:1px solid #eee;font-size:15px}
          .total{display:flex;justify-content:space-between;align-items:center;margin-top:16px;font-size:20px;color:#722F37;font-weight:700}
          .note{background:#f0f4ec;border-radius:10px;padding:12px 16px;margin-top:18px;color:#3f4a37;font-size:14px}
          .foot{color:#8a8378;font-size:12px;margin-top:24px;text-align:center}
          @media print{button{display:none}}
        </style></head><body>
        <h1>Escarg'Holala</h1><div style="color:#8a8378;font-size:13px">Confirmation de commande</div>
        <div class="num">Commande n° ${confirmed.numero}</div>
        <div class="lbl">Votre retrait</div>
        <div><strong>${esc(confirmed.point_nom)}</strong> ${esc(confirmed.point_adresse || "")}<br>${esc(confirmed.date_label)}<br>${esc(confirmed.heure_debut)} – ${esc(confirmed.heure_fin)}</div>
        <div class="lbl">Votre commande</div>
        <table><tbody>${rows}</tbody></table>
        <div class="total"><span>Total à régler sur place</span><span>${fmt(confirmed.total)}</span></div>
        <div class="note">Le règlement s'effectuera directement lors du retrait de votre commande. Aucun paiement en ligne n'est demandé.</div>
        <div class="foot">Merci pour votre commande — Escarg'Holala, Savianges, Bourgogne</div>
        <button onclick="window.print()" style="margin-top:24px;padding:11px 22px;background:#722F37;color:#fff;border:0;border-radius:22px;cursor:pointer;font-size:14px">Imprimer / enregistrer en PDF</button>
        </body></html>`);
      win.document.close();
      setTimeout(() => win.print(), 400);
    };
    return (
      <div className="min-h-screen bg-creme py-16 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <CheckCircle2 size={64} className="text-mousse mx-auto" />
          <h1 data-testid="confirm-title" className="font-heading text-4xl text-foreground mt-5">Merci pour votre commande !</h1>
          <p className="text-foreground/70 mt-3">Votre commande Escarg'Holala a bien été enregistrée.</p>

          <div className="bg-sable rounded-3xl border border-border p-7 mt-8 text-left">
            <div className="text-center pb-5 border-b border-border">
              <div className="text-sm uppercase tracking-[0.2em] text-mousse">Commande</div>
              <div data-testid="confirm-numero" className="font-heading text-3xl text-bordeaux mt-1">{confirmed.numero}</div>
            </div>
            <div className="py-5 border-b border-border">
              <div className="text-sm uppercase tracking-[0.2em] text-mousse mb-2">Votre retrait</div>
              <div className="font-600 text-foreground">{confirmed.point_nom} {confirmed.point_adresse}</div>
              <div className="text-foreground/75">{confirmed.date_label}</div>
              <div className="text-foreground/75">{confirmed.heure_debut} – {confirmed.heure_fin}</div>
            </div>
            <div className="py-5 border-b border-border">
              <div className="text-sm uppercase tracking-[0.2em] text-mousse mb-2">Votre commande</div>
              {confirmed.items.map((it, i) => (
                <div key={`${it.product_id}-${i}`} className="flex justify-between text-foreground/80 py-1">
                  <span>{it.quantite}× {it.nom} <span className="text-foreground/50 text-sm">({it.conditionnement})</span></span>
                  <span className="font-500 whitespace-nowrap">{fmt(it.sous_total)}</span>
                </div>
              ))}
            </div>
            <div className="pt-5 flex items-center justify-between">
              <span className="font-heading text-xl text-foreground">Total à régler sur place</span>
              <span data-testid="confirm-total" className="font-heading text-2xl text-bordeaux">{fmt(confirmed.total)}</span>
            </div>
          </div>

          <p className="text-foreground/70 mt-6 bg-mousse/10 rounded-xl p-4">Le règlement s'effectuera directement lors du retrait de votre commande.</p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <button onClick={printOrder} data-testid="confirm-print-btn" className="rounded-full border border-bordeaux text-bordeaux font-600 px-7 py-3 hover:bg-bordeaux hover:text-white transition-colors inline-flex items-center gap-2"><Printer size={17} /> Imprimer / enregistrer en PDF</button>
            <button onClick={() => navigate("/")} data-testid="confirm-home-btn" className="rounded-full bg-bordeaux text-white font-600 px-7 py-3 hover:bg-bordeaux-dark transition-colors">Retour au site</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-creme">
      {/* Header */}
      <header className="bg-bordeaux text-white">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
          <button onClick={() => navigate("/")} data-testid="commande-back-site" className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm"><ArrowLeft size={16} /> Site</button>
          <div className="text-center">
            <div className="font-heading text-xl leading-none">Commander vos escargots</div>
            <div className="text-white/70 text-xs mt-1">{data.campaign.nom}</div>
          </div>
          <div className="w-12" />
        </div>
      </header>

      {/* Stepper */}
      <div className="max-w-4xl mx-auto px-6 pt-6">
        <div className="flex items-center gap-1.5">
          {STEPS.map((s, i) => (
            <div key={s} className="flex-1">
              <div className={`h-1.5 rounded-full transition-colors ${i <= step ? "bg-bordeaux" : "bg-border"}`} />
              <div className={`text-[11px] mt-1.5 text-center hidden sm:block ${i === step ? "text-bordeaux font-600" : "text-foreground/40"}`}>{s}</div>
            </div>
          ))}
        </div>
        <div className="sm:hidden text-center text-sm text-bordeaux font-600 mt-2">Étape {step + 1}/{STEPS.length} — {STEPS[step]}</div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 pb-40">
        {/* STEP 0 — Produits */}
        {step === 0 && (
          <div data-testid="step-produits">
            <h2 className="font-heading text-3xl text-foreground">Je choisis mes produits</h2>
            <p className="text-foreground/65 mt-1">Règlement sur place lors du retrait · TVA non applicable, art. 293 B du CGI.</p>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {data.products.map((p) => {
                const q = qty[p.id] || 0;
                const promo = p.prix_barre && Number(p.prix_barre) > Number(p.prix);
                return (
                  <div key={p.id} data-testid={`product-card-${p.id}`} className={`relative rounded-2xl border p-5 transition-colors ${promo ? "border-bordeaux bg-bordeaux/5 ring-1 ring-bordeaux/30" : q > 0 ? "border-bordeaux bg-sable" : "border-border bg-sable"}`}>
                    {promo && (
                      <div data-testid={`promo-badge-${p.id}`} className="absolute -top-2.5 left-4 bg-bordeaux text-white text-[11px] font-700 tracking-wide uppercase px-3 py-1 rounded-full shadow-sm">
                        Offre paquet
                      </div>
                    )}
                    <div className="flex justify-between items-start gap-3">
                      <div>
                        <div className="font-600 text-foreground leading-snug">{p.nom}</div>
                        <div className="text-sm text-foreground/55">{p.conditionnement}</div>
                        {promo && (
                          <div className="text-xs font-600 text-mousse mt-1">Économisez {fmt(Number(p.prix_barre) - Number(p.prix))} sur le paquet</div>
                        )}
                      </div>
                      <div className="text-right whitespace-nowrap">
                        {promo && <div className="text-sm text-foreground/45 line-through">{fmt(p.prix_barre)}</div>}
                        <div className={`font-heading text-lg ${promo ? "text-bordeaux font-700" : "text-bordeaux"}`}>{fmt(p.prix)}</div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-4">
                      <div className="flex items-center gap-3">
                        <button data-testid={`minus-${p.id}`} onClick={() => setQ(p.id, -1)} disabled={q === 0} className="w-9 h-9 rounded-full border border-border flex items-center justify-center text-foreground hover:bg-white disabled:opacity-40 transition-colors"><Minus size={16} /></button>
                        <span data-testid={`qty-${p.id}`} className="w-8 text-center font-600 text-foreground">{q}</span>
                        <button data-testid={`plus-${p.id}`} onClick={() => setQ(p.id, 1)} className="w-9 h-9 rounded-full bg-bordeaux text-white flex items-center justify-center hover:bg-bordeaux-dark transition-colors"><Plus size={16} /></button>
                      </div>
                      {q > 0 && <span className="text-sm font-600 text-foreground">{fmt(p.prix * q)}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* STEP 1 — Point de retrait */}
        {step === 1 && (
          <div data-testid="step-point">
            <h2 className="font-heading text-3xl text-foreground">Choisissez votre point de retrait</h2>
            <div className="mt-6 space-y-3">
              {data.points.map((p) => {
                const d0 = p.dates[0];
                const hasPromo = p.image || p.presentation;
                return (
                  <div key={p.id}>
                    <button data-testid={`point-${p.id}`} onClick={() => { setPointId(p.id); setDateId(null); }}
                      className={`w-full text-left rounded-2xl border p-5 transition-colors ${pointId === p.id ? "border-bordeaux bg-bordeaux/5" : "border-border bg-sable hover:bg-white"}`}>
                      <div className="flex items-start gap-3">
                        <span className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${pointId === p.id ? "border-bordeaux" : "border-border"}`}>
                          {pointId === p.id && <span className="w-2.5 h-2.5 rounded-full bg-bordeaux" />}
                        </span>
                        <div className="flex-1">
                          <div className="font-600 text-foreground flex items-center gap-2"><MapPin size={16} className="text-bordeaux" /> {p.nom} {p.adresse && <span className="text-foreground/55 font-400">· {p.adresse}</span>}</div>
                          {d0 && <div className="text-sm text-foreground/70 mt-1">{p.dates.length > 1 ? `${p.dates.length} dates disponibles` : `${d0.label} · ${d0.heure_debut}–${d0.heure_fin}`}</div>}
                          {hasPromo && pointId !== p.id && <div className="text-xs text-mousse font-600 mt-1">Sélectionnez pour découvrir le lieu ↓</div>}
                        </div>
                      </div>
                    </button>
                    {pointId === p.id && hasPromo && (
                      <div data-testid={`point-promo-${p.id}`} className="mt-2 rounded-2xl overflow-hidden border border-bordeaux/25 bg-white">
                        {p.image && <img src={p.image} alt={p.nom} className="w-full max-h-[460px] object-contain bg-creme" />}
                        <div className="p-5">
                          <div className="text-xs uppercase tracking-[0.2em] text-mousse mb-2">Le domaine qui vous accueille</div>
                          {p.presentation && <p className="text-sm text-foreground/75 leading-relaxed whitespace-pre-line">{p.presentation}</p>}
                          {p.site_url && <a href={p.site_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm font-600 text-bordeaux mt-3 hover:underline">Visiter leur site <ArrowRight size={14} /></a>}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* STEP 2 — Date */}
        {step === 2 && selectedPoint && (
          <div data-testid="step-date">
            <h2 className="font-heading text-3xl text-foreground">Choisissez votre date de retrait</h2>
            <p className="text-foreground/65 mt-1">Point sélectionné : <strong>{selectedPoint.nom}</strong></p>
            <div className="mt-6 space-y-3">
              {selectedPoint.dates.map((d) => (
                <button key={d.id} data-testid={`date-${d.id}`} disabled={d.complet} onClick={() => setDateId(d.id)}
                  className={`w-full text-left rounded-2xl border p-5 transition-colors ${d.complet ? "border-border bg-border/30 opacity-60 cursor-not-allowed" : dateId === d.id ? "border-bordeaux bg-bordeaux/5" : "border-border bg-sable hover:bg-white"}`}>
                  <div className="flex items-center gap-3">
                    <Calendar size={18} className="text-bordeaux shrink-0" />
                    <div>
                      <div className="font-600 text-foreground">{d.label}</div>
                      <div className="text-sm text-foreground/70 flex items-center gap-1.5"><Clock size={13} /> {d.heure_debut} – {d.heure_fin}</div>
                    </div>
                    {d.complet && <span className="ml-auto text-sm font-600 text-bordeaux">Cette date est complète.</span>}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* STEP 3 — Coordonnées */}
        {step === 3 && (
          <div data-testid="step-client">
            <h2 className="font-heading text-3xl text-foreground">Vos coordonnées</h2>
            <div className="mt-6 grid sm:grid-cols-2 gap-5 bg-sable rounded-2xl border border-border p-6">
              <div className="space-y-1.5"><Label>Prénom *</Label><Input data-testid="client-prenom" value={client.prenom} onChange={(e) => setClient({ ...client, prenom: e.target.value })} placeholder="Marie" className="bg-white" /></div>
              <div className="space-y-1.5"><Label>Nom *</Label><Input data-testid="client-nom" value={client.nom} onChange={(e) => setClient({ ...client, nom: e.target.value })} placeholder="Dupont" className="bg-white" /></div>
              <div className="space-y-1.5"><Label>Téléphone *</Label><Input data-testid="client-telephone" value={client.telephone} onChange={(e) => setClient({ ...client, telephone: e.target.value })} placeholder="06 12 34 56 78" className="bg-white" />{client.telephone && !validPhone && <span className="text-xs text-bordeaux">Numéro invalide</span>}</div>
              <div className="space-y-1.5"><Label>Adresse e-mail *</Label><Input data-testid="client-email" type="email" value={client.email} onChange={(e) => setClient({ ...client, email: e.target.value })} placeholder="marie@email.fr" className="bg-white" />{client.email && !validEmail && <span className="text-xs text-bordeaux">E-mail invalide</span>}</div>
            </div>
          </div>
        )}

        {/* STEP 4 — Vérification */}
        {step === 4 && (
          <div data-testid="step-verif">
            <h2 className="font-heading text-3xl text-foreground">Vérifiez votre commande</h2>

            <div className="mt-6 bg-sable rounded-2xl border border-border p-6">
              <div className="text-sm uppercase tracking-[0.2em] text-mousse mb-3">Produits</div>
              {items.map((it) => (
                <div key={it.id} className="flex justify-between py-1.5 text-foreground/85 border-b border-border/60 last:border-0">
                  <span>{it.quantite}× {it.nom} <span className="text-foreground/50 text-sm">({it.conditionnement})</span></span>
                  <span className="font-500 whitespace-nowrap">{fmt(it.sous_total)}</span>
                </div>
              ))}
            </div>

            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              <div className="bg-sable rounded-2xl border border-border p-6">
                <div className="text-sm uppercase tracking-[0.2em] text-mousse mb-2">Retrait</div>
                <div className="font-600 text-foreground">{selectedPoint?.nom} {selectedPoint?.adresse}</div>
                <div className="text-foreground/75">{selectedDate?.label}</div>
                <div className="text-foreground/75">{selectedDate?.heure_debut} – {selectedDate?.heure_fin}</div>
              </div>
              <div className="bg-sable rounded-2xl border border-border p-6">
                <div className="text-sm uppercase tracking-[0.2em] text-mousse mb-2">Vos coordonnées</div>
                <div className="text-foreground/85">{client.prenom} {client.nom}</div>
                <div className="text-foreground/75 flex items-center gap-1.5"><Phone size={13} /> {client.telephone}</div>
                <div className="text-foreground/75 flex items-center gap-1.5"><Mail size={13} /> {client.email}</div>
              </div>
            </div>

            <div className="mt-4 bg-bordeaux text-white rounded-2xl p-6 flex items-center justify-between">
              <span className="font-heading text-xl">Total à régler sur place</span>
              <span data-testid="verif-total" className="font-heading text-3xl">{fmt(total)}</span>
            </div>

            <div className="mt-6 space-y-3">
              <label className="flex items-start gap-3 text-sm text-foreground/85 cursor-pointer">
                <input data-testid="check-verif" type="checkbox" checked={accVerif} onChange={(e) => setAccVerif(e.target.checked)} className="mt-1 w-4 h-4 accent-bordeaux" />
                <span>Je vérifie que ma commande, les quantités, le point de retrait et la date de retrait sont corrects.</span>
              </label>
              <label className="flex items-start gap-3 text-sm text-foreground/85 cursor-pointer">
                <input data-testid="check-paiement" type="checkbox" checked={accPaie} onChange={(e) => setAccPaie(e.target.checked)} className="mt-1 w-4 h-4 accent-bordeaux" />
                <span>J'ai bien compris que je réglerai ma commande sur place lors du retrait. Aucun paiement en ligne ne sera effectué.</span>
              </label>
              <label className="flex items-start gap-3 text-sm text-foreground/70 cursor-pointer">
                <input data-testid="check-rgpd" type="checkbox" checked={accRgpd} onChange={(e) => setAccRgpd(e.target.checked)} className="mt-1 w-4 h-4 accent-bordeaux" />
                <span>J'accepte que mes coordonnées soient utilisées pour le traitement de ma commande.</span>
              </label>
            </div>

            <button
              data-testid="confirm-order-btn"
              onClick={submit}
              disabled={submitting || !accVerif || !accPaie || !accRgpd}
              className="mt-7 w-full rounded-full bg-bordeaux text-white font-600 text-lg px-7 py-4 hover:bg-bordeaux-dark transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? <><Loader2 size={20} className="animate-spin" /> Enregistrement...</> : <><Check size={20} /> Confirmer ma commande</>}
            </button>
          </div>
        )}
      </div>

      {/* Sticky footer bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-border">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <div className="text-xs text-foreground/55">{items.length} produit{items.length > 1 ? "s" : ""}</div>
            <div data-testid="running-total" className="font-heading text-2xl text-bordeaux">{fmt(total)}</div>
          </div>
          <div className="flex items-center gap-3">
            {step > 0 && (
              <button data-testid="btn-back" onClick={back} className="rounded-full border border-border px-5 py-3 font-600 text-foreground hover:bg-creme transition-colors inline-flex items-center gap-1"><ArrowLeft size={16} /> Retour</button>
            )}
            {step < STEPS.length - 1 && (
              <button data-testid="btn-next" onClick={next} disabled={!canNext()} className="rounded-full bg-bordeaux text-white px-7 py-3 font-600 hover:bg-bordeaux-dark transition-colors inline-flex items-center gap-1 disabled:opacity-50">Continuer <ArrowRight size={16} /></button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Commande;
